/**
 * Debounced username availability, for the signup form and the identity
 * modal. Checked as they type, after a pause, not on submit: finding out at
 * the last tap that the name is gone is what makes people abandon a form.
 *
 * Auth optional on the endpoint, so it works before an account exists; signed
 * in, the store token goes along and a creator's own Drop slug answers as
 * available. Runs the same rules the write paths enforce, so "available" here
 * is what the save will accept.
 */
import {useEffect, useRef, useState} from "react";
import {UsersApi, UsernameAvailability, UsernameAvailabilityResponse} from "@devour/client";
import getConfig from "@/utils/getConfig";

export type UsernameCheckState =
    | { status: "idle" }
    | { status: "invalid"; message: string }
    | { status: "checking" }
    | { status: "available" }
    | { status: "taken"; suggestions: string[] }
    | { status: "notAllowed" };

/** Mirrors the backend entry rules (U9): 3–20, letters, digits, underscore, hyphen. */
export const USERNAME_PATTERN = /^[A-Za-z0-9_-]{3,20}$/;
export const USERNAME_RULES_MESSAGE = "3–20 letters, numbers, underscores or hyphens. No spaces.";

const DEBOUNCE_MS = 450;

export function useGetUsernameAvailability(value: string): UsernameCheckState {
    const [state, setState] = useState<UsernameCheckState>({status: "idle"});
    const requestId = useRef(0);

    useEffect(() => {
        const trimmed = value.trim();
        if (!trimmed) {
            setState({status: "idle"});
            return;
        }
        if (!USERNAME_PATTERN.test(trimmed)) {
            setState({status: "invalid", message: USERNAME_RULES_MESSAGE});
            return;
        }
        setState({status: "checking"});
        const id = ++requestId.current;
        const timer = window.setTimeout(async () => {
            try {
                const res: UsernameAvailabilityResponse = await new UsersApi(getConfig()).getUsersUsernameAvailability({name: trimmed});
                if (id !== requestId.current) {
                    return;
                }
                if (res.status === UsernameAvailability.AVAILABLE) {
                    setState({status: "available"});
                } else if (res.status === UsernameAvailability.TAKEN) {
                    setState({status: "taken", suggestions: res.suggestions ?? []});
                } else {
                    setState({status: "notAllowed"});
                }
            } catch {
                if (id === requestId.current) {
                    /* A failed check must not block the form; the server re-checks on submit. */
                    setState({status: "idle"});
                }
            }
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [value]);

    return state;
}
